import React, { FC, useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useRegistrationContext } from '../../contexts/RegistrationContext';
import { trackEvent } from '../../lib/analytics';
import { buildDepartmentTree } from '../../utils/departmentTree';
import { DepartmentPicker } from './DepartmentPicker';

interface DepartmentSelectionFormProps {
  onNext: () => void;
  onBack: () => void;
}

/**
 * Paso de departamento en dos niveles. Primero se eligen solo los
 * departamentos principales; si el elegido tiene subdepartamentos aparece un
 * segundo selector con solo sus hijos. El código que se envía es el del nivel
 * más bajo elegido, así el back guarda al usuario con el subdepartamento como
 * department_id (el padre se deduce de su parent_id).
 */
export const DepartmentSelectionForm: FC<DepartmentSelectionFormProps> = ({
  onNext,
  onBack,
}) => {
  const [parentCode, setParentCode] = useState('');
  const [childCode, setChildCode] = useState('');
  const { updateField } = useRegistrationContext();

  const { departments = [] } = useSelector(
    (state: RootState) => state.business
  );

  const tree = useMemo(() => buildDepartmentTree(departments), [departments]);
  const hasHierarchy = tree.some(node => node.children.length > 0);

  const selectedNode = tree.find(
    node => node.department.activationCode === parentCode
  );
  const children = selectedNode?.children ?? [];
  const needsChild = children.length > 0;
  const finalCode = needsChild ? childCode : parentCode;

  const handleParentChange = (activationCode: string) => {
    setParentCode(activationCode);
    setChildCode('');
    const node = tree.find(n => n.department.activationCode === activationCode);
    // Con subdepartamentos el código queda vacío hasta elegir el hijo.
    updateField(
      'activationCode',
      node && node.children.length === 0 ? activationCode : ''
    );
  };

  const handleChildChange = (activationCode: string) => {
    setChildCode(activationCode);
    updateField('activationCode', activationCode);
  };

  const handleNext = () => {
    trackEvent('department_selected', 'conversion', {
      has_subdepartments: hasHierarchy,
      level: needsChild ? 'child' : 'parent',
    });
    onNext();
  };

  return (
    <Box sx={{ paddingX: '5%', paddingY: '5vh' }}>
      <Button
        onClick={onBack}
        size="small"
        sx={{ marginY: '2%', fontWeight: 'bold' }}
        variant="text"
      >
        {'← Atras'}
      </Button>
      <Stack spacing={3} sx={{ marginTop: '10vh' }}>
        <DepartmentPicker
          id="department-select"
          label="Departamento"
          placeholder="Buscar departamento"
          options={tree.map(node => node.department)}
          value={parentCode}
          onChange={handleParentChange}
        />
        {needsChild && (
          <DepartmentPicker
            key={parentCode}
            id="subdepartment-select"
            label="Área"
            placeholder="Buscar área"
            options={children}
            value={childCode}
            onChange={handleChildChange}
          />
        )}
      </Stack>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={12}
      >
        <Button
          disabled={!finalCode}
          onClick={handleNext}
          size="large"
          sx={{ marginY: '5vh', fontWeight: 'bold', borderRadius: '50px' }}
          variant="contained"
        >
          {'Siguiente →'}
        </Button>
      </Stack>
    </Box>
  );
};
